import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Mail, Send, Loader2, Search, CheckCircle2, Clock, MailWarning, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  if (status === 'active') {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Active
      </Badge>
    );
  }
  if (status === 'registered') {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
        <Clock className="w-3 h-3 mr-1" /> Registered
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">
      <MailWarning className="w-3 h-3 mr-1" /> Not registered
    </Badge>
  );
};

const ParentContactsPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(null); // email currently being invited
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/parent-contacts`, getAuthHeaders());
      setContacts(res.data.contacts || []);
    } catch (e) {
      toast.error('Failed to load parent contacts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async (row) => {
    setInviting(row.email);
    try {
      const res = await axios.post(
        `${API}/auth/invite-parent-signup`,
        { email: row.email, name: row.name || '' },
        getAuthHeaders(),
      );
      if (res.data.status === 'already_active') {
        toast.info('This parent is already active');
      } else {
        toast.success(`Invite sent to ${row.email}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(null);
    }
  };

  const runBulkInvite = async () => {
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/auth/invite-parents-bulk`, {}, getAuthHeaders());
      const { sent = 0, skipped = 0, failed = 0 } = res.data;
      toast.success(`${sent} invite${sent === 1 ? '' : 's'} sent · ${skipped} skipped · ${failed} failed`);
      setBulkOpen(false);
      await load();
    } catch (e) {
      toast.error('Bulk invite failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const notRegisteredCount = contacts.filter(c => c.status === 'not_registered').length;
  const registeredCount = contacts.filter(c => c.status === 'registered').length;
  const activeCount = contacts.filter(c => c.status === 'active').length;

  const filtered = contacts.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.email?.toLowerCase().includes(q) ||
      c.name?.toLowerCase().includes(q) ||
      c.children?.some(ch => ch.toLowerCase().includes(q))
    );
  });

  return (
    <Card className="card-clay" data-testid="parent-contacts-panel">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Parent App Access
            </CardTitle>
            <CardDescription>
              <span className="inline-flex items-center gap-3 flex-wrap mt-1">
                <span><b className="text-green-700">{activeCount}</b> active</span>
                <span><b className="text-yellow-700">{registeredCount}</b> registered, never logged in</span>
                <span><b className="text-slate-700">{notRegisteredCount}</b> not registered</span>
              </span>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="parent-contacts-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              disabled={notRegisteredCount === 0}
              onClick={() => setBulkOpen(true)}
              data-testid="parent-contacts-bulk-invite"
              className="bg-primary hover:bg-primary/90"
            >
              <Send className="w-4 h-4 mr-2" /> Invite all unregistered ({notRegisteredCount})
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or child…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="parent-contacts-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">
            {search ? 'No matching parents found.' : 'No parent contacts on file yet — add children to see their parents here.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="mobile-cards">
              <TableHeader>
                <TableRow>
                  <TableHead>Parent</TableHead>
                  <TableHead>Children</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow key={row.email} data-testid={`parent-row-${row.email}`}>
                    <TableCell>
                      <div className="font-medium">{row.name || <span className="text-muted-foreground italic">no name</span>}</div>
                      <div className="text-xs text-muted-foreground">{row.email}</div>
                      {row.phone && <div className="text-xs text-muted-foreground">{row.phone}</div>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.children && row.children.length > 0
                        ? row.children.join(', ')
                        : <span className="text-muted-foreground italic">—</span>}
                    </TableCell>
                    <TableCell><StatusBadge status={row.status} /></TableCell>
                    <TableCell className="text-sm">
                      {row.status === 'active' ? (
                        <span>{formatDate(row.last_login_at)}<span className="text-muted-foreground text-xs"> · {row.login_count} logins</span></span>
                      ) : row.has_pending_invite ? (
                        <span className="text-xs text-muted-foreground">
                          Invite sent {formatDate(row.invite_last_sent_at)}
                          {row.invite_sent_count > 1 && ` (×${row.invite_sent_count})`}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.status === 'active' ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <Button
                          size="sm"
                          variant={row.has_pending_invite ? 'outline' : 'default'}
                          disabled={inviting === row.email}
                          onClick={() => sendInvite(row)}
                          data-testid={`parent-invite-${row.email}`}
                        >
                          {inviting === row.email ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Send className="w-3 h-3 mr-1" />
                          )}
                          {row.has_pending_invite ? 'Resend' : 'Send invite'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send invites to all unregistered parents?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email {notRegisteredCount} parent{notRegisteredCount === 1 ? '' : 's'} with
              a magic sign-up link. Each parent will be prompted to set a password and will land
              directly in the app. Automatic reminders will follow at day 3 and day 7 if they
              still haven&apos;t signed up.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkInvite}
              disabled={bulkLoading}
              data-testid="bulk-invite-confirm"
            >
              {bulkLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send {notRegisteredCount} invite{notRegisteredCount === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ParentContactsPanel;
